import React from 'react';
import DataGrid, {
  Column, Editing, Lookup, Paging, SearchPanel,
  Toolbar,
  Pager, Scrolling,
  Item,
  RequiredRule,
  StringLengthRule
} from 'devextreme-react/data-grid';
import { Paper, Typography } from '@mui/material';

import { useDxStore } from '../../../../hooks/useDxStore';
import { useAccountsLookupStore } from './hooks';
import { RowUpdatingEvent } from 'devextreme/ui/data_grid';

const defaultPageSize = 10;
const allowedPageSizes = [10, 25, 50];

export default function BlobSettings() {
  const blobStore = useDxStore(
    {
      url: '/companies/tcam/blob-url',
      loadUrl: '/companies/tcam/blob-url/list',
      insertUrl: '/companies/tcam/blob-url/add',
      updateUrl: '/companies/tcam/blob-url/edit',
      removeUrl: '/companies/tcam/blob-url/delete',
      keyField: 'id'
    }
  );
  const accountsLookupStore = useAccountsLookupStore();

  const handleRowUpdating = (e: RowUpdatingEvent<Partial<{id: any}>>) => {
    e.newData = {
      ...e.oldData,
      ...e.newData,
    };
  };

  return (
    <Paper sx={{ width: '100%' }} elevation={2}>
      <DataGrid
        id="gridContainer"
        dataSource={blobStore}
        allowColumnReordering={true}
        rowAlternationEnabled={true}
        repaintChangesOnly={true}
        customizeColumns={(columns) => {
          columns.forEach(col => {
            col.alignment = 'left';
          });
        }}
        showBorders={true}
        onRowUpdating={handleRowUpdating}
      >
        <Editing
          mode="row"
          allowUpdating={true}
          allowDeleting={true}
          allowAdding={true}
          useIcons={true}
        />
        <Scrolling mode="standard" />
        <Toolbar
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
          elementAttr={{ class: 'dx-toolbar-custom' }}
        >
          <Item
            location="before"
            locateInMenu="never"
          >
            <Typography
              data-test="titleTable"
              variant="h5"
              sx={{ marginRight: 2 }}
            >
              Blob Settings
            </Typography>
          </Item>
          <Item
            name="addRowButton"
            location="after"
          />
          <Item
            name="searchPanel"
            location="after"
            cssClass={'dx-searchinput-custom'}
          />
        </Toolbar>
        <SearchPanel visible={true} highlightCaseSensitive={true} />
        <Paging
          defaultPageSize={defaultPageSize}
          enabled={true}
        />
        <Pager
          allowedPageSizes={allowedPageSizes}
          showNavigationButtons={true}
        />
        <Column
          dataField="blobUrl"
          caption="URL"
          minWidth={400}
        >
          <RequiredRule />
          <StringLengthRule
            max={400}
            message="The input is more than 400 characters long"
          />
        </Column>
        <Column
          dataField="type"
          caption="Type"
          minWidth={100}
          lookup={{
            dataSource: ['PROD', 'LIVE', 'TEST']
          }}
        >
          <RequiredRule />
        </Column>
        <Column
          dataField="blobRegion"
          caption="Region"
          minWidth={100}
        >
          <RequiredRule />
          <StringLengthRule
            max={100}
            message="The input is more than 100 characters long"
          />
        </Column>
        <Column
          dataField="accountId"
          caption="Account"
          minWidth={225}
          cellRender={(cell) => {
            if (cell.data.accountId == null) {
              return null;
            }
            return <span>{cell.data.accountName}</span>;
          }}
        >
          <Lookup
            dataSource={{
              store: accountsLookupStore,
              paginate: true,
              pageSize: 10,
            }}
            valueExpr="id"
            displayExpr="name"
          />
        </Column>
      </DataGrid>
    </Paper>
  );
}
